import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { MessageSquare, X, Send, Loader2, Trash2, Stethoscope, CalendarClock, AlertCircle } from 'lucide-react'
import { nameInitial } from '../../lib/text.js'
import { isAdmin } from '../../lib/role.js'
import ConfirmDialog from '../ConfirmDialog.jsx'
import { onChatMessage } from '../../lib/chatDock.js'
import {
  fetchThread, sendMessage, deleteMessage, markRead,
  setUnread, clearThreadLocally,
  isMine, isDeleted, readersOf, chatTime, bkkDay, dayLabel,
} from '../../lib/chat.js'

/**
 * ข้อความ — the front desk <-> treatment room chat for ONE patient.
 *
 * ── Why it is a DOCK and not a tab ──────────────────────────────────────────
 * A chat's job is to be available *while* you do something else. Sending the
 * doctor to `/patient/:id/chat` mid-note is the same failure the chart's
 * no-auto-refresh rule exists to prevent — they are in the middle of an Hx and
 * the thing they need to read is one navigation away from the thing they are
 * writing. So: a panel that overlays (or, from xl up, sits beside) whatever
 * patient sub-page is open, triggered from the patient header.
 *
 * ── Why the trigger is in the HEADER ────────────────────────────────────────
 * The realistic failure of this feature is a message about the WRONG PATIENT —
 * the front desk keeps four patient tabs open. Welding the thread to the row
 * that carries the name, HN and allergy chips is what makes that hard. A
 * floating bottom-right bubble would also collide with the chart save bar and
 * the z-50 drawers.
 *
 * ── Why it is rendered at the LAYOUT ROOT ───────────────────────────────────
 * `PatientHeader` lives inside `sticky … z-20`, which is a stacking context: a
 * panel nested there could not rise above the billing/lab drawers no matter its
 * z-index. Same lesson `PatientNoteAlert` already carries. Only the BUTTON is
 * in the header.
 *
 * ── Not the medical record ──────────────────────────────────────────────────
 * Stated in the footer, on purpose and permanently. If chat is easier than the
 * chart (it is), then in a year the chart says nothing and the truth lives in a
 * log nobody re-reads. Anything that must be remembered goes in the chart.
 */
export default function PatientChatDock({
  patientId, patientName, treatmentId = 0, me, live, open, onClose,
}) {
  const [data, setData] = useState(null)     // { items, readers, next_page, total }
  const [err, setErr] = useState('')
  const [page, setPage] = useState(1)
  const [older, setOlder] = useState([])     // pages 2..n, oldest-last
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [sendErr, setSendErr] = useState('')
  const [confirmDel, setConfirmDel] = useState(null)
  const [pending, setPending] = useState([])  // optimistic, not yet acknowledged

  const scrollRef = useRef(null)
  const inputRef = useRef(null)
  const reqId = useRef(0)

  /* ── load ──────────────────────────────────────────────────────────────── */
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!patientId) return
    const id = ++reqId.current
    if (!quiet) { setErr(''); setData(null) }
    try {
      const r = await fetchThread(patientId, { page: 1, perPage: 50 })
      if (id !== reqId.current) return
      setData(r)
      setErr('')
      // Anything the server now knows about is no longer "pending".
      setPending((p) => p.filter((m) => !(r.items || []).some((s) => s.body === m.body && String(s.user_id) === String(m.user_id))))
    } catch (e) {
      if (id !== reqId.current) return
      // null data, NOT [] — an errored thread must never read as an empty one.
      setErr(e?.body?.detail || e?.message || 'โหลดข้อความไม่สำเร็จ')
      if (!quiet) setData(null)
    }
  }, [patientId])

  // Open → load, mark read, focus the box. Closing does NOT clear the thread:
  // reopening on the same patient should not flash a spinner.
  useEffect(() => {
    if (!open) return
    setPage(1); setOlder([])
    load()
    clearThreadLocally(patientId)
    markRead(patientId).then((r) => { if (r?.unread) setUnread(r.unread) }).catch(() => {})
    const t = setTimeout(() => inputRef.current?.focus(), 120)
    return () => clearTimeout(t)
  }, [open, patientId, load])

  // A different patient while closed — drop the old thread so it can never be
  // shown under the new patient's name for a frame.
  useEffect(() => { if (!open) { setData(null); setPending([]) } }, [patientId]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Live: `type:"chat_message"` rides the ONE `patient_update/{id}` subscription
   * PatientLayout already owns, so this panel opens no socket of its own.
   *
   * 🚨 The type is `chat_message`, never `chat` — one letter from `chart`, the
   * type that raises the chart detail's stale-note pill.
   *
   * Refetch rather than splice — a splice would have to reproduce the server's
   * tombstone and read-receipt rules, in a second place.
   */
  const absorb = useCallback(() => {
    load({ quiet: true })
    clearThreadLocally(patientId)
    markRead(patientId).then((r) => { if (r?.unread) setUnread(r.unread) }).catch(() => {})
  }, [load, patientId])

  useEffect(() => {
    if (live?.type !== 'chat_message') return
    if (open) absorb()
  }, [live]) // eslint-disable-line react-hooks/exhaustive-deps

  // Second path, and the one that is actually proven to arrive: ChatToaster owns
  // the only chat_inbox subscription and re-broadcasts every inbound message on
  // the bus, and the dock listens here. (That split dodged the old same-name
  // channel collision; lib/realtime.js now shares one channel per name, so it is
  // no longer required — just proven.)
  useEffect(() => {
    if (!open) return
    return onChatMessage((p) => {
      if (String(p?.patient_id) !== String(patientId)) return
      absorb()
    })
  }, [open, patientId, absorb])

  /**
   * Grow the box to fit, up to the cap.
   *
   * `overflowY` is switched explicitly: a textarea defaults to `auto`, and
   * scrollHeight routinely lands a pixel or two above the height we set from it,
   * so a one-line message was enough to raise a scrollbar inside the input.
   * Hidden until the content genuinely passes the cap.
   */
  const MAX_INPUT_H = 140
  function autoGrow(el) {
    if (!el) return
    el.style.height = 'auto'
    const h = Math.min(el.scrollHeight, MAX_INPUT_H)
    el.style.height = `${h}px`
    el.style.overflowY = el.scrollHeight > MAX_INPUT_H ? 'auto' : 'hidden'
  }

  // Send clears `text`; the box has to shrink back with it.
  useEffect(() => { if (!text) autoGrow(inputRef.current) }, [text])

  async function loadOlder() {
    if (loadingOlder || !data?.next_page) return
    setLoadingOlder(true)
    try {
      const next = older.length ? page + 1 : 2
      const r = await fetchThread(patientId, { page: next, perPage: 50 })
      setOlder((o) => [...o, ...(r.items || [])])
      setPage(next)
      setData((d) => (d ? { ...d, next_page: r.next_page } : d))
    } catch (e) {
      setErr(e?.message || 'โหลดข้อความเก่าไม่สำเร็จ')
    } finally {
      setLoadingOlder(false)
    }
  }

  /* ── send ──────────────────────────────────────────────────────────────── */
  async function send() {
    const body = text.trim()
    if (!body || sending) return
    setSending(true); setSendErr('')
    // Optimistic: the clinic's wifi is not fast and a message that appears half
    // a second after Enter reads as "did that send?".
    const optimistic = {
      id: `tmp-${Date.now()}`,
      body,
      created_at: Date.now(),
      user_id: me?.xano_id || 0,
      author_name: me?.code || me?.name || '',
      author_role: me?.doctor_id && !me?.employee_id ? 'doctor' : 'staff',
      treatment_id: treatmentId || 0,
      active: true,
      _pending: true,
    }
    setPending((p) => [...p, optimistic])
    setText('')
    try {
      await sendMessage({ patientId, treatmentId, body })
      await load({ quiet: true })
    } catch (e) {
      setPending((p) => p.filter((m) => m.id !== optimistic.id))
      setText(body)          // never swallow what they typed
      setSendErr(e?.body?.detail || e?.message || 'ส่งไม่สำเร็จ')
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  async function doDelete(m) {
    setConfirmDel(null)
    try {
      await deleteMessage(m.id)
      await load({ quiet: true })
    } catch (e) {
      setErr(e?.body?.detail || e?.message || 'ลบไม่สำเร็จ')
    }
  }

  /* ── shape for rendering ───────────────────────────────────────────────── */
  // Server gives newest-first, page by page: page 1 is the newest 50, page 2 the
  // 50 before those. So the full newest-first run is page1 ++ page2 ++ … and the
  // reverse of that is the conversation. (Putting `older` first would interleave
  // last week between yesterday and today.)
  const messages = useMemo(() => {
    const base = [...(data?.items || []), ...older].reverse()
    return [...base, ...pending]
  }, [data, older, pending])

  const readers = data?.readers || []
  // The receipt goes on the LAST of my messages that anyone has read — one mark
  // at the bottom of my side, not a tick on every line.
  const lastReadMineId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (!isMine(m, me) || m._pending) continue
      if (readersOf(m, readers).length) return m.id
    }
    return null
  }, [messages, readers, me])

  // Stick to the bottom on new content, but never yank the view while someone
  // is reading back through history.
  const stickRef = useRef(true)
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el || !open) return
    if (stickRef.current) el.scrollTop = el.scrollHeight
  }, [messages.length, open])

  function onScroll() {
    const el = scrollRef.current
    if (!el) return
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  if (!open) return null

  return (
    <>
      {/* Backdrop — only below xl, where the dock overlays instead of sitting beside. */}
      <div
        className="xl:hidden fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px] animate-ss-pop-in"
        onMouseDown={onClose}
        aria-hidden
      />

      <aside
        className="fixed z-50 inset-y-0 right-0 w-full sm:w-[400px] bg-white border-l border-slate-200 shadow-2xl xl:shadow-none flex flex-col"
        role="complementary"
        aria-label="ข้อความเกี่ยวกับผู้ป่วยรายนี้"
      >
        {/* Header — carries the patient name, so which thread this is can never
            be in doubt even when four patient tabs are open. */}
        <div className="flex items-start gap-2.5 px-4 py-3 border-b border-slate-200/80 flex-none">
          <span className="mt-0.5 w-8 h-8 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center flex-none">
            <MessageSquare size={16} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[14.5px] font-semibold text-slate-900 leading-tight">ข้อความ</div>
            <div className="text-[12.5px] text-slate-500 truncate leading-tight mt-0.5">{patientName || '—'}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="ปิด"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-none"
          >
            <X size={16} strokeWidth={2.25} />
          </button>
        </div>

        {/* Thread */}
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
          {data == null && !err && (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 size={18} className="animate-spin" />
            </div>
          )}

          {err && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2 text-[13px] text-amber-800">
              <AlertCircle size={14} className="mt-0.5 flex-none" />
              <span className="break-words">{err}</span>
            </div>
          )}

          {data && data.next_page && (
            <div className="flex justify-center pb-2">
              <button
                type="button"
                onClick={loadOlder}
                disabled={loadingOlder}
                className="text-[12.5px] text-slate-500 hover:text-brand-700 px-3 py-1 rounded-full hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                {loadingOlder ? 'กำลังโหลด…' : 'ดูข้อความก่อนหน้า'}
              </button>
            </div>
          )}

          {data && messages.length === 0 && (
            <div className="text-center py-10 px-4">
              <p className="text-[13.5px] text-slate-500">ยังไม่มีข้อความเกี่ยวกับคนไข้รายนี้</p>
              <p className="text-[12.5px] text-slate-400 mt-1 leading-relaxed">
                ใช้คุยกันระหว่างเคาน์เตอร์กับห้องตรวจ เช่น คนไข้ขอเลื่อนนัด สอบถามราคา หรือแจ้งสิทธิ์
              </p>
            </div>
          )}

          {messages.map((m, i) => {
            const prev = messages[i - 1]
            const day = bkkDay(m.created_at)
            const newDay = !prev || bkkDay(prev.created_at) !== day
            return (
              <div key={m.id ?? i}>
                {newDay && (
                  <div className="flex items-center gap-2 py-2.5">
                    <span className="h-px flex-1 bg-slate-100" />
                    <span className="text-[11.5px] text-slate-400 font-medium">{dayLabel(day)}</span>
                    <span className="h-px flex-1 bg-slate-100" />
                  </div>
                )}
                <Bubble
                  m={m}
                  me={me}
                  showReceipt={m.id === lastReadMineId}
                  receipt={readersOf(m, readers)}
                  onDelete={() => setConfirmDel(m)}
                  canDelete={isMine(m, me) || isAdmin(me)}
                />
              </div>
            )
          })}
        </div>

        {/* Composer */}
        <div className="flex-none border-t border-slate-200/80 px-3 pt-2.5 pb-3">
          {sendErr && (
            <div className="mb-2 text-[12.5px] text-rose-600 break-words">{sendErr}</div>
          )}
          {treatmentId > 0 && (
            <div className="mb-1.5 inline-flex items-center gap-1 text-[11.5px] text-slate-400">
              <CalendarClock size={11} strokeWidth={2} />
              จะแนบกับการรักษาที่เปิดอยู่
            </div>
          )}
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              rows={1}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line — the traffic here is
                // one-liners, and reaching for a Send button on every one is friction.
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() }
              }}
              onInput={(e) => autoGrow(e.currentTarget)}
              maxLength={2000}
              placeholder="พิมพ์ข้อความ…"
              className="flex-1 min-w-0 resize-none overflow-x-hidden break-words rounded-xl border border-slate-200 px-3 py-2 text-[14px] leading-relaxed placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 transition"
            />
            <button
              type="button"
              onClick={send}
              disabled={!text.trim() || sending}
              aria-label="ส่ง"
              className="flex-none w-9 h-9 rounded-xl bg-brand-600 text-white flex items-center justify-center hover:bg-brand-700 disabled:opacity-40 disabled:hover:bg-brand-600 transition-colors"
            >
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} strokeWidth={2} />}
            </button>
          </div>
          <p className="mt-2 text-[11.5px] text-slate-400 leading-snug">
            Shift + Enter ขึ้นบรรทัดใหม่
          </p>
        </div>
      </aside>

      {confirmDel && (
        <ConfirmDialog
          title="ลบข้อความนี้?"
          message="ข้อความจะหายไปจากบทสนทนา แต่ยังเหลือร่องรอยว่าถูกลบ — และแก้ไขไม่ได้"
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => doDelete(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}
    </>
  )
}

/* ── one message ──────────────────────────────────────────────────────────── */

function Bubble({ m, me, showReceipt, receipt, onDelete, canDelete }) {
  const mine = isMine(m, me)
  const gone = isDeleted(m)
  const doctor = m.author_role === 'doctor'

  if (gone) {
    return (
      <div className={`flex ${mine ? 'justify-end' : 'justify-start'} py-0.5`}>
        <span className="text-[12px] text-slate-400 italic px-3 py-1 rounded-lg bg-slate-50 ring-1 ring-slate-100">
          ลบข้อความแล้ว
        </span>
      </div>
    )
  }

  return (
    <div className={`group flex gap-2 py-0.5 ${mine ? 'flex-row-reverse' : ''}`}>
      {!mine && (
        <span
          title={m.author_name}
          className={`mt-0.5 w-7 h-7 rounded-full flex-none flex items-center justify-center text-[11.5px] font-semibold ring-1 ${
            doctor ? 'bg-violet-50 text-violet-700 ring-violet-100' : 'bg-slate-100 text-slate-600 ring-slate-200'
          }`}
        >
          {doctor ? <Stethoscope size={13} strokeWidth={2} /> : nameInitial(m.author_name || '?')}
        </span>
      )}

      <div className={`min-w-0 max-w-[78%] ${mine ? 'items-end text-right' : ''} flex flex-col`}>
        {!mine && (
          <span className="text-[11.5px] text-slate-500 px-1 mb-0.5">{m.author_name || '—'}</span>
        )}
        <div
          className={`relative rounded-2xl px-3 py-1.5 text-[14px] leading-relaxed break-words text-left ${
            mine
              ? 'bg-brand-600 text-white rounded-br-md'
              : doctor
                ? 'bg-violet-50 text-slate-800 ring-1 ring-violet-100 rounded-bl-md'
                : 'bg-slate-100 text-slate-800 rounded-bl-md'
          } ${m._pending ? 'opacity-60' : ''}`}
        >
          {/* pre-wrap: staff type multi-line notes, and collapsing them is how a
              two-line instruction becomes one unreadable run. */}
          <p className="whitespace-pre-wrap">{m.body}</p>
        </div>

        <div className={`flex items-center gap-1.5 px-1 mt-0.5 ${mine ? 'justify-end' : ''}`}>
          <span className="text-[11px] text-slate-400 tabular-nums">
            {m._pending ? 'กำลังส่ง…' : chatTime(m.created_at)}
          </span>
          {m.treatment_id > 0 && (
            <span
              title="ส่งจากหน้าการรักษานี้"
              className="inline-flex items-center gap-0.5 text-[10.5px] text-slate-400"
            >
              <CalendarClock size={9.5} strokeWidth={2} />#{m.treatment_id}
            </span>
          )}
          {canDelete && !m._pending && (
            <button
              type="button"
              onClick={onDelete}
              aria-label="ลบข้อความ"
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-300 hover:text-rose-600 transition-opacity"
            >
              <Trash2 size={11.5} strokeWidth={2} />
            </button>
          )}
        </div>

        {/* The front desk's whole question is "did the doctor see it". Without
            this they walk to the room anyway and the feature earns nothing. */}
        {showReceipt && receipt.length > 0 && (
          <span className="text-[11px] text-slate-400 px-1">
            อ่านแล้ว · {receipt.slice(0, 3).join(', ')}{receipt.length > 3 ? ` +${receipt.length - 3}` : ''}
          </span>
        )}
      </div>
    </div>
  )
}
